/**
 * Generic TanStack `filterFn` factories for the metadata-driven column filters.
 *
 * Each returns a `FilterFn` that interprets the value stored in `columnFilters`
 * according to the column's `ColumnFilterSpec.type`. They are domain-agnostic and
 * only depend on the raw cell value, so any DataTable column can opt in by attaching
 * the matching spec + the filterFn returned by `resolveColumnFilterFn(spec)`.
 */
import type { FilterFn } from "@tanstack/react-table";
import type {
  ColumnFilterRangeValue,
  ColumnFilterSpec,
  ColumnFilterValue,
} from "./columnFilter.types";

/** Accent + case folding for EN/FR-friendly substring matching (diacritics only, no translation). */
export function foldAccents(str: string): string {
  return str
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();
}

/** Coerce a cell value to a searchable string (joins arrays of strings). */
function toSearchString(value: unknown): string {
  if (value == null) return "";
  if (Array.isArray(value)) return value.map(toSearchString).join(" ");
  return String(value);
}

/** Lowercased array of cell values (wraps single values into a one-element array). */
function cellAsLowerArray(raw: unknown): string[] {
  return (Array.isArray(raw) ? raw : [raw])
    .filter((v) => v != null)
    .map((v) => String(v).toLowerCase());
}

/** `text` — accent-insensitive substring match on the cell value. */
export const textFilterFn: FilterFn<unknown> = (row, columnId, filterValue) => {
  const q = foldAccents(String(filterValue ?? "").trim());
  if (!q) return true;
  return foldAccents(toSearchString(row.getValue(columnId))).includes(q);
};

/** `multiSelect` (ANY) — cell array contains at least one selected value. */
export const multiSelectAnyFilterFn: FilterFn<unknown> = (
  row,
  columnId,
  filterValue
) => {
  const selected = (filterValue as string[] | undefined) ?? [];
  if (selected.length === 0) return true;
  const cell = cellAsLowerArray(row.getValue(columnId));
  return selected.some((s) => cell.includes(s.toLowerCase()));
};

/** `multiSelect` (ALL) — cell array contains every selected value. */
export const multiSelectAllFilterFn: FilterFn<unknown> = (
  row,
  columnId,
  filterValue
) => {
  const selected = (filterValue as string[] | undefined) ?? [];
  if (selected.length === 0) return true;
  const cell = cellAsLowerArray(row.getValue(columnId));
  return selected.every((s) => cell.includes(s.toLowerCase()));
};

/** `enum` — cell value equals ANY selected option. */
export const enumFilterFn: FilterFn<unknown> = (row, columnId, filterValue) => {
  const selected = (filterValue as string[] | undefined) ?? [];
  if (selected.length === 0) return true;
  const cell = String(row.getValue(columnId) ?? "");
  return selected.includes(cell);
};

/** `range` — numeric cell value falls within [min, max] inclusive. */
export const rangeFilterFn: FilterFn<unknown> = (row, columnId, filterValue) => {
  const range = filterValue as ColumnFilterRangeValue | undefined;
  if (!range) return true;
  const value = Number(row.getValue(columnId));
  if (Number.isNaN(value)) return false;
  return value >= range.min && value <= range.max;
};

/**
 * Pick the correct `filterFn` for a spec. Attach the result to the column def's
 * `filterFn` so a column needs only its `meta.filter` spec + this one call to filter.
 */
export function resolveColumnFilterFn<TData = unknown>(
  spec: ColumnFilterSpec
): FilterFn<TData> {
  let fn: FilterFn<unknown>;
  switch (spec.type) {
    case "text":
      fn = textFilterFn;
      break;
    case "multiSelect":
      fn = spec.matchMode === "all" ? multiSelectAllFilterFn : multiSelectAnyFilterFn;
      break;
    case "enum":
      fn = enumFilterFn;
      break;
    case "range":
      fn = rangeFilterFn;
      break;
    default:
      fn = textFilterFn;
  }
  // The underlying filter fns are domain-agnostic (they only read cell values via
  // row.getValue), so the row generic is safe to widen to the caller's TData.
  return fn as FilterFn<TData>;
}

/** True when a stored filter value should be treated as "no active filter". */
export function isColumnFilterEmpty(
  spec: ColumnFilterSpec,
  value: ColumnFilterValue | undefined
): boolean {
  if (value == null) return true;
  switch (spec.type) {
    case "text":
      return String(value).trim().length === 0;
    case "multiSelect":
    case "enum":
      return !Array.isArray(value) || value.length === 0;
    case "range": {
      const r = value as ColumnFilterRangeValue;
      const min = spec.min ?? 0;
      const max = spec.max ?? 0;
      // Full-span range = inactive.
      return r.min <= min && r.max >= max;
    }
    default:
      return true;
  }
}
